import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Divider,
  message,
  Popconfirm,
  Row,
  Spin,
  Typography,
} from "antd";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { loadHome } from "../../../application/home-slice";
import { useAppDispatch, useAppSelector } from "../../../application/store";
import {
  HomeRepository,
  HomeSection,
  HomeSectionImage,
  SliderItem,
} from "../../../domain";
import { ServiceLocater, Services } from "../../../service-locater";
import I18n from "../../i18n";
import EditHomeSectionImageModal from "./components/EditHomeSectionImageModal";
import EditHomeSectionInfoModal from "./components/EditHomeSectionInfoModal";
import EditSliderItemMoal from "./components/EditSliderItemMoal";
import NewHomeSectionModal from "./components/NewHomeSectionModal";
import NewSliderItemMoal from "./components/NewSliderItemMoal";

export default function HomeSettings() {
  const dispatch = useAppDispatch();
  const { loading, slider, sections } = useAppSelector((state) => state.home);

  const stableDispatch = useCallback(dispatch, []);
  useEffect(() => {
    stableDispatch(loadHome());
  }, [stableDispatch]);

  const [newSliderItemModalVisible, setNewSliderItemModalVisible] = useState(
    false
  );
  const openNewSliderItemModal = () => setNewSliderItemModalVisible(true);
  const closeNewSliderItemModal = () => setNewSliderItemModalVisible(false);

  const [editSliderItemModal, setEditSliderItemModal] = useState<{
    visible: boolean;
    sliderItem?: SliderItem;
  }>({ visible: false });
  const openEditSliderItemModal = (sliderItem: SliderItem) =>
    setEditSliderItemModal({ visible: true, sliderItem });
  const closeEditSliderItemModal = () =>
    setEditSliderItemModal({ visible: false, sliderItem: undefined });

  const [deleteSlideLoading, setDeleteSlideLoading] = useState(false);
  const deleteSlide = async (id: string) => {
    setDeleteSlideLoading(true);
    const hide = message.loading("در حال حذف اسلاید...");
    const res = await ServiceLocater.get<HomeRepository>(
      Services.HomeRepository
    ).deleteSliderItem(id);
    setDeleteSlideLoading(false);
    hide();
    res.fold(
      (_) => {
        message.error(I18n.general.networkError);
      },
      (_) => {
        message.success("اسلاید با موفقیت حذف شد");
        dispatch(loadHome());
      }
    );
  };

  const [deleteSectionLoading, setDeleteSectionLoading] = useState(false);
  const deleteSection = async (id: string) => {
    setDeleteSectionLoading(true);
    const hide = message.loading("در حال حذف ردیف...");
    const res = await ServiceLocater.get<HomeRepository>(
      Services.HomeRepository
    ).deleteHomeSection(id);
    setDeleteSectionLoading(false);
    hide();
    res.fold(
      (_) => {
        message.error(I18n.general.networkError);
      },
      (_) => {
        message.success("ردیف با موفقیت حذف شد");
        dispatch(loadHome());
      }
    );
  };

  const [newHomeSectionModalVisible, setNewHomeSectionModalVisible] = useState(
    false
  );
  const openNewHomeSectionModal = () => setNewHomeSectionModalVisible(true);
  const closeNewHomeSectionModal = () => setNewHomeSectionModalVisible(false);

  const [editHomeSectionInfoModal, setEditHomeSectionInfoModal] = useState<{
    visible: boolean;
    section?: HomeSection;
  }>({ visible: false });
  const openEditHomeSectionInfoModal = (section: HomeSection) =>
    setEditHomeSectionInfoModal({ visible: true, section });
  const closeEditHomeSectionInfoModal = () =>
    setEditHomeSectionInfoModal({ visible: false, section: undefined });

  const [editHomeSectionImageModal, setEditHomeSectionImageModal] = useState<{
    visible: boolean;
    image?: HomeSectionImage;
  }>({ visible: false });
  const openEditHomeSectionImageModal = (image: HomeSectionImage) =>
    setEditHomeSectionImageModal({ visible: true, image });
  const closeEditHomeSectionImageModal = () =>
    setEditHomeSectionImageModal({ visible: false, image: undefined });

  return (
    <Spin spinning={loading !== "succeeded"}>
      <div style={{ padding: 16 }}>
        <Typography.Title level={4}>اسلایدر</Typography.Title>

        <Row>
          {slider.map((item) => {
            return (
              <Col key={item.id} sm={4} style={{ margin: "0 4px" }}>
                <img src={item.image} width="100%" alt={item.link} />
                <a href={item.link}>{item.link}</a>
                <Row>
                  <Button
                    type="link"
                    onClick={() => openEditSliderItemModal(item)}
                    disabled={deleteSlideLoading}
                  >
                    <EditOutlined />
                  </Button>
                  <Popconfirm
                    title="آیا از حذف این اسلاید اطمینان دارید؟"
                    onConfirm={() => deleteSlide(item.id)}
                  >
                    <Button type="link" danger disabled={deleteSlideLoading}>
                      <DeleteOutlined />
                    </Button>
                  </Popconfirm>
                </Row>
              </Col>
            );
          })}
          {loading === "succeeded" && slider.length < 5 ? (
            <Col sm={4} onClick={() => openNewSliderItemModal()}>
              <Row justify="center" align="stretch">
                <PlusOutlined style={{ fontSize: 32, marginTop: "40%" }} />
              </Row>
            </Col>
          ) : (
            <Fragment />
          )}
        </Row>

        <Divider />
        <Typography.Title level={4}>ردیف‌ها</Typography.Title>

        {sections.map((section) => {
          return section.type === "Scroll" ? (
            <Col key={section.id}>
              <Row justify="space-between">
                <Typography.Title level={5}>{section.title}</Typography.Title>
                <div>
                  <Popconfirm
                    title="از حذف این ردیف اطمینان دارید؟"
                    onConfirm={() => deleteSection(section.id)}
                  >
                    <Button type="link" danger disabled={deleteSectionLoading}>
                      <DeleteOutlined />
                    </Button>
                  </Popconfirm>
                  <Button
                    type="link"
                    onClick={() => openEditHomeSectionInfoModal(section)}
                  >
                    <EditOutlined />
                  </Button>
                </div>
              </Row>
              <Row>
                {section.items.map((item) => {
                  return (
                    <Col
                      key={item.product.id}
                      sm={4}
                      style={{ paddingLeft: 8 }}
                    >
                      <img
                        src={item.product.images[0].url}
                        width="100%"
                        alt={item.product.title}
                      />
                      <div>
                        <Typography.Text>{item.product.title}</Typography.Text>
                      </div>
                      <div>
                        <Typography.Text>
                          {item.product.attributes[0].price.toLocaleString()}{" "}
                          تومان
                        </Typography.Text>
                      </div>
                    </Col>
                  );
                })}
              </Row>
              <Divider />
            </Col>
          ) : (
            <Col key={section.id}>
              <Row justify="space-between">
                <Typography.Title level={5}>{section.title}</Typography.Title>
                <div>
                  <Popconfirm
                    title="از حذف این ردیف اطمینان دارید؟"
                    onConfirm={() => deleteSection(section.id)}
                  >
                    <Button type="link" danger disabled={deleteSectionLoading}>
                      <DeleteOutlined />
                    </Button>
                  </Popconfirm>
                  <Button
                    type="link"
                    onClick={() => openEditHomeSectionInfoModal(section)}
                  >
                    <EditOutlined />
                  </Button>
                </div>
              </Row>
              <Typography.Paragraph>{section.description}</Typography.Paragraph>
              {section.isActive}
              <Row>
                {section.images.map((item) => {
                  return (
                    <Col key={item.id} sm={6} style={{ paddingLeft: 8 }}>
                      <img src={item.image} width="100%" alt={item.link} />
                      <a href={item.link}>{item.link}</a>
                      <Button
                        type="link"
                        onClick={() => openEditHomeSectionImageModal(item)}
                      >
                        <EditOutlined />
                      </Button>
                    </Col>
                  );
                })}
              </Row>
              <Divider />
            </Col>
          );
        })}
        {loading === "succeeded" && sections.length < 5 ? (
          <Row
            justify="center"
            align="stretch"
            onClick={() => openNewHomeSectionModal()}
          >
            <PlusOutlined style={{ fontSize: 32 }} />
          </Row>
        ) : (
          <Fragment />
        )}
      </div>

      <NewSliderItemMoal
        visible={newSliderItemModalVisible}
        onClose={closeNewSliderItemModal}
      />

      <EditSliderItemMoal
        visible={editSliderItemModal.visible}
        onClose={closeEditSliderItemModal}
        sliderItem={editSliderItemModal.sliderItem}
      />

      <NewHomeSectionModal
        visible={newHomeSectionModalVisible}
        onClose={closeNewHomeSectionModal}
      />

      <EditHomeSectionInfoModal
        visible={editHomeSectionInfoModal.visible}
        onClose={closeEditHomeSectionInfoModal}
        section={editHomeSectionInfoModal.section}
      />

      <EditHomeSectionImageModal
        visible={editHomeSectionImageModal.visible}
        onClose={closeEditHomeSectionImageModal}
        image={editHomeSectionImageModal.image}
      />
    </Spin>
  );
}
